// @ts-nocheck
import React, { useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import * as services from "../Services/services";
import LearningLesson from "./LearningLesson";
import LearningCatalog from "./LearningCatalog";
import LearningTranscript from "./LearningTranscript";
import LearningAdmin from "./LearningAdmin";
import LearningSignup from "./LearningSignup";
import { useLoad, Status, DownloadButton } from "./learningShared";
import { formatDue, formatMinutes } from "../utils/lessonMedia";
import { hasCertificate } from "../utils/lmsLearner";
import "./Learning.css";

/*
 * Training (beta) — the learner side of the AvSight LMS, replacing AvSight University (Northpass).
 *
 * Courses, paths and lessons are authored in AVS OS and served by it. Everything goes through
 * copilot-api /apis/copilot/lms/*, which forwards who the person is; AVS OS decides what they may see.
 * The contract is AVS OS's docs/architecture/learning-copilot-api.md.
 *
 * The home view has tabs (launch, Scott 2026-10-16): Assigned (what's on my list), View All (the
 * catalog, with Enroll while self-enrolment is on), My transcript, and Team for a customer's
 * training admin. Someone Copilot signed in who has no training record gets the sign-up request form.
 *
 * One route, /learning, with the view in the query string — ?tab=, ?path=, ?course=, ?lesson= —
 * because Main matches exact pathnames (the same choice as /knowledge-base-2?article=).
 */

function Progress({ done, total, testid }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="d-flex align-items-center gap-2" data-testid={testid}>
      <div className="progress flex-grow-1 learning-progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className={`progress-bar ${pct === 100 ? "bg-success" : ""}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="small text-body-secondary text-nowrap">
        {done} of {total}
      </span>
    </div>
  );
}

function StatusIcon({ status, available }) {
  // The icon is decoration; the words beside it are for screen readers.
  const [icon, word] = !available
    ? ["fa-regular fa-clock text-body-secondary", "Coming soon"]
    : status === "completed"
      ? ["fa-solid fa-circle-check text-success", "Done"]
      : status === "in_progress"
        ? ["fa-solid fa-circle-half-stroke text-primary", "In progress"]
        : ["fa-regular fa-circle text-body-secondary", "Not started"];
  return (
    <>
      <i className={icon} title={word} aria-hidden="true" />
      <span className="visually-hidden">{word}: </span>
    </>
  );
}

function LessonList({ course, onOpenLesson }) {
  return (
    <ol className="list-unstyled mb-0 learning-lessons">
      {course.items.map((item, i) => (
        <li key={item.topicId} className="d-flex align-items-center gap-2 py-2 border-top">
          <StatusIcon status={item.status} available={item.available} />
          {item.available ? (
            <button
              type="button"
              className="btn btn-link p-0 text-start text-decoration-none flex-grow-1"
              onClick={() => onOpenLesson(item.lessonId, course.courseId)}
              data-testid={`learning-lesson-link-${i}`}
            >
              {item.title}
            </button>
          ) : (
            <span className="text-body-secondary flex-grow-1">{item.title} <span className="small">(coming soon)</span></span>
          )}
          {item.hasVideo && (
            <>
              <i className="fa-solid fa-circle-play text-body-secondary" title="Has a video" aria-hidden="true" />
              <span className="visually-hidden">Has a video</span>
            </>
          )}
          {item.estimatedMinutes && <span className="small text-body-secondary text-nowrap">{formatMinutes(item.estimatedMinutes)}</span>}
        </li>
      ))}
    </ol>
  );
}

function CourseCard({ course, onOpenCourse, onOpenLesson, compact = false }) {
  const finished = hasCertificate(course);
  return (
    <div className="card mb-3 learning-card" data-testid="learning-course-card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
          <div>
            <button
              type="button"
              className="btn btn-link p-0 text-start text-decoration-none h6 mb-1 learning-title"
              onClick={() => onOpenCourse(course.courseId)}
              data-testid="learning-course-open"
            >
              {course.courseName}
            </button>
            <div className="small text-body-secondary">
              {[
                `${course.lessons} lesson${course.lessons === 1 ? "" : "s"}`,
                formatMinutes(course.estimatedMinutes),
                course.dueAt ? `Due ${formatDue(course.dueAt)}` : null,
              ].filter(Boolean).join(" · ")}
              {course.overdue && <span className="badge text-bg-danger ms-2">Overdue</span>}
            </div>
          </div>
          {!finished && course.next && (
            <button
              type="button"
              className="btn btn-primary btn-sm text-nowrap"
              onClick={() => onOpenLesson(course.next.lessonId, course.courseId)}
              data-testid="learning-course-continue"
            >
              {course.completed > 0 ? "Continue" : "Start"}
            </button>
          )}
          {finished && (
            <div className="d-flex flex-column align-items-end gap-2">
              <span className="badge text-bg-success">Complete</span>
              {hasCertificate(course) && (
                <DownloadButton
                  kind="certificate"
                  id={course.courseId}
                  fileLabel={course.courseName}
                  label={<>Certificate<span className="visually-hidden"> for {course.courseName}</span></>}
                  icon="fa-solid fa-award"
                  testid="learning-course-certificate"
                />
              )}
            </div>
          )}
        </div>
        <Progress done={course.completed} total={course.lessons} testid="learning-course-progress" />
        {!compact && course.items.length > 0 && (
          <div className="mt-3">
            <LessonList course={course} onOpenLesson={onOpenLesson} />
          </div>
        )}
      </div>
    </div>
  );
}

function PathCard({ path, onOpenPath, onOpenLesson }) {
  const finished = path.courseCount > 0 && path.completedCourses === path.courseCount;
  return (
    <div className="card mb-3 learning-card learning-path-card" data-testid="learning-path-card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
          <div>
            <div className="small text-uppercase text-body-secondary fw-semibold mb-1">Learning path</div>
            <button
              type="button"
              className="btn btn-link p-0 text-start text-decoration-none h5 mb-1 learning-title"
              onClick={() => onOpenPath(path.pathId)}
              data-testid="learning-path-open"
            >
              {path.name}
            </button>
            {path.description && <p className="text-body-secondary mb-1">{path.description}</p>}
            <div className="small text-body-secondary">
              {[`${path.courseCount} course${path.courseCount === 1 ? "" : "s"}`, path.dueAt ? `Due ${formatDue(path.dueAt)}` : null]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
          {!finished && path.next?.next && (
            <button
              type="button"
              className="btn btn-primary btn-sm text-nowrap"
              onClick={() => onOpenLesson(path.next.next.lessonId, path.next.courseId)}
              data-testid="learning-path-continue"
            >
              {path.completedLessons > 0 ? "Continue" : "Start"}
            </button>
          )}
          {finished && <span className="badge text-bg-success">Complete</span>}
        </div>
        <Progress done={path.completedCourses} total={path.courseCount} testid="learning-path-progress" />
      </div>
    </div>
  );
}

function BackLink({ label, onClick }) {
  return (
    <button type="button" className="btn btn-link px-0 mb-3 text-decoration-none" onClick={onClick} data-testid="learning-back">
      <i className="fa-solid fa-arrow-left me-2" aria-hidden="true" />
      {label}
    </button>
  );
}

function Empty({ canBrowse, onBrowse }) {
  return (
    <div className="text-center py-5 text-body-secondary" data-testid="learning-empty">
      <i className="fa-solid fa-graduation-cap fa-2x mb-3 d-block" aria-hidden="true" />
      <p className="mb-1">Nothing has been assigned to you yet.</p>
      <p className="small mb-0">
        When your team assigns you a course or learning path, it will show up here.
        {canBrowse && (
          <>
            {" "}
            <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={onBrowse}>
              Browse everything you can take
            </button>
            .
          </>
        )}
      </p>
    </div>
  );
}

function Assigned({ data, nav, onBrowse }) {
  // A course on a path is shown on the path, not again underneath it.
  const loose = useMemo(() => {
    const onPaths = new Set((data.paths || []).flatMap((p) => p.courses.map((c) => c.courseId)));
    return (data.courses || []).filter((c) => !onPaths.has(c.courseId));
  }, [data]);

  return (
    <>
      {data.paths.length === 0 && data.courses.length === 0 && <Empty canBrowse onBrowse={onBrowse} />}
      {data.paths.length > 0 && (
        <section className="mb-4" data-testid="learning-paths" aria-label="Learning paths">
          {data.paths.map((p) => (
            <PathCard key={p.pathId} path={p} onOpenPath={nav.path} onOpenLesson={nav.lesson} />
          ))}
        </section>
      )}
      {loose.length > 0 && (
        <section data-testid="learning-courses" aria-label="Courses">
          {data.paths.length > 0 && <h2 className="h6 text-body-secondary text-uppercase mb-3">Courses</h2>}
          {loose.map((c) => (
            <CourseCard key={c.courseId} course={c} onOpenCourse={nav.course} onOpenLesson={nav.lesson} />
          ))}
        </section>
      )}
    </>
  );
}

const TABS = [
  { id: "assigned", label: "Assigned" },
  { id: "all", label: "View All" },
  { id: "transcript", label: "My transcript" },
  { id: "team", label: "Team", adminOnly: true },
];

/** The tabs this person gets. Team only for a customer's training admin (AVS OS says so in /me). */
export function tabsFor(me) {
  return TABS.filter((t) => !t.adminOnly || me?.isTrainingAdmin === true);
}

/**
 * A WAI-ARIA tab list: arrow keys move between tabs (and select, the "automatic activation" pattern),
 * Home/End jump to the ends, and only the selected tab is in the Tab order.
 */
function Tabs({ tabs, current, onSelect }) {
  const refs = useRef({});
  const onKey = (e, i) => {
    const last = tabs.length - 1;
    const to = e.key === "ArrowRight" ? (i === last ? 0 : i + 1) : e.key === "ArrowLeft" ? (i === 0 ? last : i - 1) : e.key === "Home" ? 0 : e.key === "End" ? last : null;
    if (to === null) return;
    e.preventDefault();
    onSelect(tabs[to].id);
    refs.current[tabs[to].id]?.focus();
  };
  return (
    <ul className="nav nav-tabs mb-3" role="tablist" aria-label="Training">
      {tabs.map((t, i) => {
        const selected = t.id === current;
        return (
          <li className="nav-item" role="presentation" key={t.id}>
            <button
              ref={(el) => (refs.current[t.id] = el)}
              type="button"
              role="tab"
              id={`learning-tab-${t.id}`}
              aria-selected={selected}
              aria-controls={`learning-panel-${t.id}`}
              tabIndex={selected ? 0 : -1}
              className={`nav-link ${selected ? "active" : ""}`}
              onClick={() => onSelect(t.id)}
              onKeyDown={(e) => onKey(e, i)}
              data-testid={`learning-tab-${t.id}`}
            >
              {t.label}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Overview({ nav, tab }) {
  const { loading, error, code, data, reload } = useLoad(() => services.lmsOverview(), []);

  // No training record for this person: offer to ask their company's training admin.
  if (code === "no_learner")
    return (
      <>
        <h1 className="h4 mb-3">Training</h1>
        <LearningSignup />
      </>
    );

  const tabs = tabsFor(data);
  const current = tabs.some((t) => t.id === tab) ? tab : "assigned";

  return (
    <>
      <h1 className="h4 mb-1">{current === "team" ? "Your team's training" : "My training"}</h1>
      <p className="text-body-secondary mb-3">Courses and learning paths for you{data?.isTrainingAdmin ? " and your team" : ""}.</p>
      <Status loading={loading} error={error} onRetry={reload} />
      {data && (
        <>
          <Tabs tabs={tabs} current={current} onSelect={nav.tab} />
          <div role="tabpanel" id={`learning-panel-${current}`} aria-labelledby={`learning-tab-${current}`} tabIndex={0} className="learning-tabpanel">
            {current === "assigned" && <Assigned data={data} nav={nav} onBrowse={() => nav.tab("all")} />}
            {current === "all" && <LearningCatalog nav={nav} onEnrolled={reload} />}
            {current === "transcript" && <LearningTranscript />}
            {current === "team" && <LearningAdmin />}
          </div>
        </>
      )}
    </>
  );
}

function PathView({ id, nav }) {
  const { loading, error, data, reload } = useLoad(() => services.lmsPath(id), [id]);
  return (
    <>
      <BackLink label="My training" onClick={nav.home} />
      <Status loading={loading} error={error} onRetry={reload} />
      {data && (
        <>
          <div className="small text-uppercase text-body-secondary fw-semibold mb-1">Learning path</div>
          <h1 className="h4 mb-2">{data.name}</h1>
          {data.description && <p className="text-body-secondary">{data.description}</p>}
          <div className="mb-4">
            <Progress done={data.completedCourses} total={data.courseCount} testid="learning-path-progress" />
          </div>
          {data.courses.map((c, i) => (
            <div key={c.courseId} className="d-flex gap-3">
              <div className="learning-step-number text-body-secondary fw-semibold pt-3">{i + 1}</div>
              <div className="flex-grow-1">
                <CourseCard course={c} onOpenCourse={nav.course} onOpenLesson={nav.lesson} compact />
              </div>
            </div>
          ))}
        </>
      )}
    </>
  );
}

function CourseView({ id, nav }) {
  const { loading, error, data, reload } = useLoad(() => services.lmsCourse(id), [id]);
  return (
    <>
      <BackLink label="My training" onClick={nav.home} />
      <Status loading={loading} error={error} onRetry={reload} />
      {data && (
        <>
          <h1 className="h4 mb-2">{data.courseName}</h1>
          {(data.intro || data.description) && <p className="text-body-secondary learning-prose">{data.intro || data.description}</p>}
          <CourseCard course={data} onOpenCourse={() => {}} onOpenLesson={nav.lesson} />
        </>
      )}
    </>
  );
}

export default function Learning() {
  const [params, setParams] = useSearchParams();
  const pathId = params.get("path");
  const courseId = params.get("course");
  const lessonId = params.get("lesson");
  const tab = params.get("tab");

  const go = useCallback(
    (next) => {
      setParams(next);
      window.scrollTo?.(0, 0);
    },
    [setParams]
  );

  const nav = useMemo(
    () => ({
      home: () => go({}),
      tab: (id) => go(id && id !== "assigned" ? { tab: id } : {}),
      path: (id) => go({ path: id }),
      course: (id) => go({ course: id }),
      // The course travels with the lesson so "Next lesson" and "Back to course" know where they are.
      lesson: (id, inCourse) => go(inCourse ? { lesson: id, course: inCourse } : { lesson: id }),
    }),
    [go]
  );

  return (
    <div className="container-fluid py-3 px-3 px-md-4 learning-page" data-testid="learning-page">
      {lessonId ? (
        <LearningLesson key={lessonId} lessonId={lessonId} courseId={courseId} nav={nav} />
      ) : pathId ? (
        <PathView id={pathId} nav={nav} />
      ) : courseId ? (
        <CourseView id={courseId} nav={nav} />
      ) : (
        <Overview nav={nav} tab={tab} />
      )}
    </div>
  );
}
